import React, { useMemo } from 'react'

/**
 * COMPONENTE: BONGO CAM OVERLAY (VISUAL CHILD)
 * ------------------------------------------------------------------
 * Renderiza los bongos tropicales e interactivos usando SVGs puros
 * y animaciones CSS3 aceleradas por hardware.
 * ------------------------------------------------------------------
 */
const Bongo = ({ config, leftHitActive = false, rightHitActive = false, intensityLevel = 0, comboCount = 0, shaking = false }) => {
    const {
        enabled,
        visible,
        bongoSkin, // 'classic-wood', 'neon-cyberpunk', 'soccer-special'
        hitEffect, // 'shockwave', 'electric-spark', 'bubbles', 'none'
        hitColor,
        bgPreset, // 'tropical-sunset', 'cyber-grid', 'solid-dark'
        decoStyle, // 'beach-tropical', 'wild-jungle', 'concert-silhouettes', 'none'
        floatingShape, // 'fireflies', 'tropical-leaves', 'notes', 'balls', 'maracas'
        shapeOpacity,
        shapeGlow,
        scale,
        offsetX,
        offsetY
    } = config;

    // Convertir Hex a RGBA
    const hexToRgba = (hex, alpha = 0.5) => {
        if (!hex) return `rgba(0, 255, 255, ${alpha})`;
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
        return result 
            ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})`
            : `rgba(0, 255, 255, ${alpha})`;
    };

    const primaryGlow = hexToRgba(hitColor || '#00ffff', 0.5);
    const primaryGlowStrong = hexToRgba(hitColor || '#00ffff', 0.9);

    // Clases del overlay
    const overlayClasses = `bongo-overlay-container ${visible && enabled ? 'reveal' : 'hide-overlay'} bg-${bgPreset} ${shaking ? 'screen-shake' : ''}`;

    // Estilos dinámicos
    const cssVariablesStyle = {
        '--hit-color': hitColor || '#00ffff',
        '--hit-glow-soft': primaryGlow,
        '--hit-glow-strong': primaryGlowStrong,
        '--shape-opacity': shapeOpacity ?? 0.3,
        '--shape-glow': `${shapeGlow || 15}px`,
        '--bongo-scale': scale || 1.0,
        '--offset-x': `${offsetX || 0}px`,
        '--offset-y': `${offsetY || 0}px`
    };

    // Render de figuras flotantes (incluye Maracas)
    const getShapeSvg = (type) => {
        switch (type) {
            case 'fireflies':
                return (
                    <svg viewBox="0 0 100 100" fill="currentColor">
                        <circle cx="50" cy="50" r="25" />
                    </svg>
                );
            case 'tropical-leaves':
                return (
                    <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M50 90 C30 70 20 40 50 10 C80 40 70 70 50 90 Z M50 10 V90 M35 45 C40 43 45 42 50 42 M65 45 C60 43 55 42 50 42 M30 65 C40 60 45 58 50 58 M70 65 C60 60 55 58 50 58" fill="currentColor" fillOpacity="0.2" />
                    </svg>
                );
            case 'notes':
                return (
                    <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                        <path d="M35 75 V25 H75 V65" />
                        <circle cx="23" cy="75" r="12" fill="currentColor" />
                        <circle cx="63" cy="65" r="12" fill="currentColor" />
                    </svg>
                );
            case 'balls':
                return (
                    <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="50" cy="50" r="40" />
                        <path d="M22 30 L50 10 L78 30 L67 60 L33 60 Z M50 10 V50 M22 30 L50 50 M78 30 L50 50 M67 60 L50 50 M33 60 L50 50" />
                    </svg>
                );
            case 'maracas':
                return (
                    <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        {/* Maraca 1 */}
                        <path d="M35 60 L20 85" />
                        <rect x="25" y="20" width="22" height="35" rx="11" fill="currentColor" fillOpacity="0.2" />
                        {/* Maraca 2 */}
                        <path d="M65 60 L80 85" />
                        <rect x="53" y="20" width="22" height="35" rx="11" fill="currentColor" fillOpacity="0.2" />
                        {/* Pequeñas líneas de sonido */}
                        <path d="M12 25 L5 20 M10 40 L3 42 M88 25 L95 20 M90 40 L97 42" />
                    </svg>
                );
            default:
                return null;
        }
    };

    const floatingElements = useMemo(() => {
        const list = [];
        for (let i = 0; i < 15; i++) {
            list.push({
                id: i,
                left: Math.random() * 90 + 5,
                delay: Math.random() * 8,
                duration: Math.random() * 6 + 8, // 8s a 14s
                size: Math.random() * 30 + 20 // 20px a 50px
            });
        }
        return list;
    }, [floatingShape]);

    // Render de bongos con SVG o PNG
    const renderBongoSvg = (isActive, side) => {
        if (bongoSkin === 'classic-wood') {
            return (
                <img 
                    src={side === 'left' ? "/img/overlays/bongo/1.png" : "/img/overlays/bongo/2.png"} 
                    alt={`Bongo ${side}`}
                    className={`bongo-img ${isActive ? 'bongo-active-pulse' : ''}`}
                    style={{
                        width: '100%',
                        display: 'block',
                        filter: isActive 
                            ? 'brightness(1.5) drop-shadow(0px 0px 20px rgba(0, 255, 255, 1))' 
                            : 'drop-shadow(0px 0px 10px rgba(0,0,0,0.8))',
                        transition: 'transform 0.05s',
                        transform: isActive ? 'scale(0.95) translateY(5px)' : 'none'
                    }}
                />
            );
        }

        const isNeon = bongoSkin === 'neon-cyberpunk';
        const isSoccer = bongoSkin === 'soccer-special';
        
        let strokeColor = isNeon ? 'var(--hit-color)' : '#4a2c11';
        let fillColor = isNeon ? '#0f111a' : '#8b5a2b';
        let headColor = isNeon ? '#1b2234' : '#f5deb3';

        if (isSoccer) {
            strokeColor = '#1e293b';
            fillColor = '#f8fafc';
            headColor = '#0f172a';
        }

        if (isActive) {
            strokeColor = 'var(--hit-color)';
            fillColor = isNeon ? '#1c283d' : '#a06d3b';
            headColor = '#ffffff';
        }

        return (
            <svg 
                viewBox="0 0 100 100" 
                className={`bongo-vector-img ${isActive ? 'bongo-active-pulse' : ''}`}
                style={{ 
                    filter: isActive 
                        ? 'drop-shadow(0 0 25px var(--hit-color)) brightness(1.3)' 
                        : 'drop-shadow(0 4px 10px rgba(0,0,0,0.5))',
                    width: '100%',
                    display: 'block',
                    transition: 'transform 0.05s'
                }}
            >
                {/* Cuerpo del Bongo */}
                <path d="M15 20 C15 20 20 85 30 90 H70 C80 85 85 20 85 20 Z" fill={fillColor} stroke={strokeColor} strokeWidth="3" />
                {/* Cuero Superior */}
                <ellipse cx="50" cy="20" rx="35" ry="10" fill={headColor} stroke={strokeColor} strokeWidth="3" />
                
                {/* Detalles de madera/metal o futbol */}
                {!isSoccer ? (
                    <>
                        <line x1="30" y1="20" x2="33" y2="87" stroke={strokeColor} strokeWidth="2" />
                        <line x1="70" y1="20" x2="67" y2="87" stroke={strokeColor} strokeWidth="2" />
                        <line x1="50" y1="20" x2="50" y2="90" stroke={strokeColor} strokeWidth="2" />
                        <path d="M30 50 H70" stroke={strokeColor} strokeWidth="3" fill="none" />
                    </>
                ) : (
                    <>
                        {/* Pentágonos estilo balón */}
                        <path d="M40 35 L50 28 L60 35 L56 46 L44 46 Z" fill="#000" fillOpacity="0.75" />
                        <line x1="40" y1="35" x2="25" y2="38" stroke={strokeColor} strokeWidth="2" />
                        <line x1="60" y1="35" x2="75" y2="38" stroke={strokeColor} strokeWidth="2" />
                        <line x1="44" y1="46" x2="35" y2="65" stroke={strokeColor} strokeWidth="2" />
                        <line x1="56" y1="46" x2="65" y2="65" stroke={strokeColor} strokeWidth="2" />
                    </>
                )}
            </svg>
        );
    };

    return (
        <div className={overlayClasses} style={cssVariablesStyle}>
            <style>{`
                .bongo-overlay-container {
                    position: absolute;
                    top: 0; left: 0;
                    width: 100vw; height: 100vh;
                    overflow: hidden;
                    pointer-events: none;
                    background-color: transparent !important;
                    transition: opacity 0.8s ease-in-out;
                    z-index: 10;
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-end;
                    align-items: center;
                }

                .bongo-overlay-container.hide-overlay {
                    opacity: 0;
                }

                /* 1. SCENARIO / FONDOS */
                .bongo-scenario-bg {
                    position: absolute;
                    top: 0; left: 0;
                    width: 100%; height: 100%;
                    z-index: -15;
                }

                .bg-tropical-sunset .bongo-scenario-bg {
                    background-image: url('/img/overlays/bongo/fondo.png');
                    background-size: cover;
                    background-position: center bottom;
                }

                .bg-cyber-grid .bongo-scenario-bg {
                    background: radial-gradient(circle, rgba(0, 255, 255, 0.1) 20%, rgba(120, 0, 255, 0.25) 100%);
                }

                /* Pulso global de fondo */
                .bongo-pulse-glow {
                    position: absolute;
                    top: 0; left: 0;
                    width: 100%; height: 100%;
                    background: radial-gradient(circle, var(--hit-glow-soft) 20%, transparent 80%);
                    opacity: 0;
                    z-index: -10;
                    transition: opacity 0.1s ease-out;
                }

                .left-active .bongo-pulse-glow,
                .right-active .bongo-pulse-glow {
                    opacity: 0.35;
                }

                /* 2. FIGURAS FLOTANTES */
                .bongo-floating-shape {
                    position: absolute;
                    bottom: -80px;
                    color: var(--hit-color);
                    opacity: var(--shape-opacity);
                    filter: drop-shadow(0 0 var(--shape-glow) var(--hit-color));
                    z-index: 5;
                    will-change: transform;
                    animation: floatBongoUp linear infinite;
                }

                @keyframes floatBongoUp {
                    0% { transform: translateY(0) scale(0.6) rotate(0deg); opacity: 0; }
                    20% { opacity: var(--shape-opacity); }
                    80% { opacity: var(--shape-opacity); }
                    100% { transform: translateY(-110vh) scale(1.3) rotate(270deg); opacity: 0; }
                }

                /* 3. ESCENARIO CENTRAL BONGOS */
                .bongo-stage {
                    display: flex;
                    gap: 60px;
                    position: relative;
                    z-index: 10;
                    transform: translate(var(--offset-x), var(--offset-y)) scale(var(--bongo-scale));
                    transform-origin: bottom center;
                    margin-bottom: 4vh;
                    will-change: transform;
                }

                .bongo-inner-box {
                    position: relative;
                    width: 250px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                /* Aro ovalado vertical del prototipo (Imagen 2) */
                .bongo-neon-oval {
                    position: absolute;
                    top: -15%; left: 0%;
                    width: 100%; height: 130%;
                    border: 5px solid var(--hit-color);
                    border-radius: 50%;
                    box-shadow: 0 0 25px var(--hit-color), inset 0 0 20px var(--hit-color);
                    opacity: 0;
                    transform: scale(0.92);
                    transition: opacity 0.05s ease-out, transform 0.05s ease-out;
                    pointer-events: none;
                    z-index: 5;
                }

                .bongo-neon-oval.oval-active {
                    opacity: 1;
                    transform: scale(1);
                    animation: bongo-oval-hit 0.3s cubic-bezier(0.1, 0.8, 0.3, 1) forwards;
                }

                @keyframes bongo-oval-hit {
                    0% { transform: scale(0.92); opacity: 1; }
                    50% { transform: scale(1.04); opacity: 1; }
                    100% { transform: scale(1); opacity: 1; }
                }

                /* Onda expansiva debajo del congo */
                .bongo-wave-pulse {
                    position: absolute;
                    top: 20%; left: 10%;
                    width: 80%; height: 80%;
                    border-radius: 50%;
                    border: 6px solid var(--hit-color);
                    box-shadow: 0 0 25px var(--hit-color), inset 0 0 20px var(--hit-color);
                    transform: scale(0.7);
                    opacity: 0;
                    z-index: 1;
                    pointer-events: none;
                    will-change: transform, opacity;
                }

                /* Disparo de impacto */
                .bongo-wave-active {
                    animation: bongoWaveAnim 0.38s cubic-bezier(0.1, 0.8, 0.3, 1) forwards;
                }

                @keyframes bongoWaveAnim {
                    0% { transform: scale(0.7); opacity: 1; border-width: 8px; }
                    100% { transform: scale(2.0); opacity: 0; border-width: 1px; }
                }

                /* Resplandor del parche superior (Imagen 1) */
                .bongo-head-glow {
                    position: absolute;
                    top: 12%; left: 10%;
                    width: 80%; height: 18%;
                    background: radial-gradient(ellipse at center, var(--hit-color) 0%, rgba(255,255,255,0.8) 30%, transparent 70%);
                    border-radius: 50%;
                    opacity: 0;
                    pointer-events: none;
                    z-index: 6;
                    mix-blend-mode: screen;
                    transition: opacity 0.05s ease-out;
                }

                .bongo-head-glow.head-glow-active {
                    opacity: 0.95;
                    filter: drop-shadow(0 0 10px var(--hit-color));
                }

                /* Rebote de bongo */
                .bongo-vector-img.bongo-active-pulse,
                .bongo-img.bongo-active-pulse {
                    transform: scale(0.94) translateY(6px) !important;
                }

                /* 4. DECORACIONES DE ESCENARIO */
                .bongo-decorations-fg {
                    position: absolute;
                    bottom: 0; left: 0;
                    width: 100%; height: 16vh;
                    z-index: 8;
                    pointer-events: none;
                }

                /* Siluetas de vegetación */
                .deco-beach-tropical,
                .deco-wild-jungle {
                    background: linear-gradient(180deg, transparent 0%, rgba(10,12,18,0.9) 80%);
                }

                .beach-tropical-item {
                    position: absolute;
                    bottom: 0;
                    width: 22vh; height: 26vh;
                    fill: #06070a;
                    opacity: 0.95;
                }

                /* 5. SISTEMA DE COMBO CENTRAL */
                .bongo-combo-hud {
                    position: absolute;
                    top: -15vh;
                    left: 50%;
                    transform: translateX(-50%);
                    text-align: center;
                    z-index: 12;
                    pointer-events: none;
                    width: 100%;
                }

                .bongo-combo-text {
                    font-family: 'Impact', sans-serif;
                    font-size: 3.5rem;
                    color: #ffffff;
                    margin: 0;
                    text-shadow: 0 0 15px var(--hit-color), 0 0 30px var(--hit-color);
                    animation: bongoPop 0.15s ease-out forwards;
                    will-change: transform, opacity;
                }

                @keyframes bongoPop {
                    0% { transform: scale(0.6); opacity: 0; }
                    75% { transform: scale(1.15); opacity: 1; }
                    100% { transform: scale(1); opacity: 1; }
                }

                /* 6. VIBRACIÓN DE PANTALLA (SHAKE) */
                .screen-shake {
                    animation: shakeBongo 0.15s infinite;
                }

                @keyframes shakeBongo {
                    0% { transform: translate(1px, 1px); }
                    20% { transform: translate(-2px, -1px); }
                    40% { transform: translate(-1px, 1px); }
                    60% { transform: translate(2px, -1px); }
                    80% { transform: translate(-1px, -2px); }
                    100% { transform: translate(1px, 1px); }
                }
            `}</style>

            {/* Escenario ambientación */}
            <div className="bongo-scenario-bg" />
            <div className="bongo-pulse-glow" />

            {/* Luciérnagas / Maracas flotantes */}
            {floatingElements.map(el => {
                const shapeColor = el.id % 2 === 0 ? '#00ffff' : '#ff00ff';
                const shapeGlowVal = config.shapeGlow !== undefined ? config.shapeGlow : 15;
                return (
                    <div
                        key={el.id}
                        className="bongo-floating-shape"
                        style={{
                            width: `${el.size}px`,
                            height: `${el.size}px`,
                            left: `${el.left}%`,
                            color: shapeColor,
                            filter: `drop-shadow(0 0 ${shapeGlowVal}px ${shapeColor})`,
                            animationDuration: `${el.duration}s`,
                            animationDelay: `${el.delay}s`
                        }}
                    >
                        {getShapeSvg(floatingShape)}
                    </div>
                );
            })}

            {/* Central Bongos */}
            <div className={`bongo-stage ${leftHitActive ? 'left-active' : ''} ${rightHitActive ? 'right-active' : ''}`}>
                
                {/* HUD de Combos */}
                {intensityLevel === 4 && comboCount > 0 && (
                    <div className="bongo-combo-hud">
                        <div key={comboCount} className="bongo-combo-text">
                            Combo x{comboCount}
                        </div>
                    </div>
                )}

                {/* Bongo Izquierdo */}
                <div className="bongo-inner-box">
                    <div className={`bongo-neon-oval ${leftHitActive ? 'oval-active' : ''}`} />
                    <div className={`bongo-head-glow ${leftHitActive ? 'head-glow-active' : ''}`} />
                    <div className={`bongo-wave-pulse ${leftHitActive && hitEffect === 'shockwave' ? 'bongo-wave-active' : ''} ${intensityLevel >= 2 ? 'red-wave' : ''}`} />
                    {renderBongoSvg(leftHitActive, 'left')}
                </div>

                {/* Bongo Derecho */}
                <div className="bongo-inner-box">
                    <div className={`bongo-neon-oval ${rightHitActive ? 'oval-active' : ''}`} />
                    <div className={`bongo-head-glow ${rightHitActive ? 'head-glow-active' : ''}`} />
                    <div className={`bongo-wave-pulse ${rightHitActive && hitEffect === 'shockwave' ? 'bongo-wave-active' : ''} ${intensityLevel >= 2 ? 'red-wave' : ''}`} />
                    {renderBongoSvg(rightHitActive, 'right')}
                </div>
            </div>

            {/* Vegetación decorativa frontal */}
            {decoStyle === 'beach-tropical' && (
                <div className="bongo-decorations-fg deco-beach-tropical">
                    {/* SVG Silueta de Palmeras Izquierda */}
                    <svg className="beach-tropical-item" style={{ left: '3%' }} viewBox="0 0 100 100">
                        <path d="M10 100 Q15 60 40 40 Q45 35 48 30 C30 35 15 50 10 70 M48 30 C55 35 65 50 70 70 M48 30 C45 20 30 15 15 15 M48 30 C52 20 68 15 80 15" fill="#08080c" />
                    </svg>
                    {/* SVG Silueta de Palmeras Derecha */}
                    <svg className="beach-tropical-item" style={{ right: '3%', transform: 'scaleX(-1)' }} viewBox="0 0 100 100">
                        <path d="M10 100 Q15 60 40 40 Q45 35 48 30 C30 35 15 50 10 70 M48 30 C55 35 65 50 70 70 M48 30 C45 20 30 15 15 15 M48 30 C52 20 68 15 80 15" fill="#08080c" />
                    </svg>
                </div>
            )}
            
            {decoStyle === 'wild-jungle' && (
                <div className="bongo-decorations-fg deco-wild-jungle">
                    {/* Hojas gigantes en esquinas */}
                    <svg className="beach-tropical-item" style={{ left: '-2vw', height: '30vh' }} viewBox="0 0 100 100">
                        <path d="M0 100 Q30 80 40 40 C45 20 20 5 0 0 C15 25 22 55 0 75" fill="#040508" />
                    </svg>
                    <svg className="beach-tropical-item" style={{ right: '-2vw', transform: 'scaleX(-1)', height: '30vh' }} viewBox="0 0 100 100">
                        <path d="M0 100 Q30 80 40 40 C45 20 20 5 0 0 C15 25 22 55 0 75" fill="#040508" />
                    </svg>
                </div>
            )}
        </div>
    );
};

export { Bongo };
export default Bongo;
